import React, { FC, useState } from "react";

import { inject, observer } from "mobx-react";
import { useNavigate, NavLink } from "react-router-dom";
import { Button, Drawer, Tag } from "antd";
import { LogoutOutlined, MenuOutlined } from "@ant-design/icons";

import BrandLogo from "../BrandLogo";

interface Props {
  store?: any;
}

const ADMIN_LINKS = [
  { to: "/admin/manage", label: "Manage" },
  { to: "/admin/billing", label: "Billing" },
  { to: "/admin/coupons", label: "Coupons" },
];

const SUPER_LINKS = [
  { to: "/admin/events", label: "Events" },
  { to: "/admin/users", label: "Users" },
  { to: "/admin/clients", label: "Clients" },
];

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  "text-sm font-medium no-underline transition-colors " + (isActive ? "text-white" : "text-neutral-400 hover:text-white");

const Header: FC<Props> = inject("store")(
  observer((props) => {
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const links = props.store.isSuper ? [...ADMIN_LINKS, ...SUPER_LINKS] : ADMIN_LINKS;

    const closeAndGo = (to: string) => {
      setDrawerOpen(false);
      navigate(to);
    };

    return (
      <header className="sticky top-0 z-40 bg-header-bg shadow-md pt-[env(safe-area-inset-top)]">
        <div className="flex items-center h-16 md:h-20 px-4 md:px-6">
          <BrandLogo surface="dark" imgClassName="h-12 md:h-16 w-auto" className="cursor-pointer" onClick={() => navigate("/")} />

          {props.store.accessToken && (
            <>
              {/* Desktop admin nav */}
              <nav className="ml-auto hidden md:flex items-center gap-5">
                {links.map((link) => (
                  <NavLink key={link.to} to={link.to} className={navLinkClass}>
                    {link.label}
                  </NavLink>
                ))}
                {props.store.isSuper && (
                  <Tag color="gold" className="mr-0 font-bold">
                    SUPER
                  </Tag>
                )}
                <span className="text-neutral-400 text-xs border border-solid border-neutral-700 rounded px-2 py-0.5">
                  {props.store.clientRef}
                </span>
                <Button
                  type="text"
                  className="text-neutral-300 hover:text-white"
                  icon={<LogoutOutlined />}
                  onClick={props.store.signOut}
                  aria-label="Sign out"
                />
              </nav>

              {/* Mobile admin nav */}
              <button
                className="ml-auto md:hidden bg-transparent border-0 text-white text-xl p-2 cursor-pointer"
                onClick={() => setDrawerOpen(true)}
                aria-label="Open menu"
              >
                <MenuOutlined />
              </button>
              <Drawer title={props.store.clientRef || "Admin"} placement="right" width={260} open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <div className="flex flex-col gap-1">
                  {links.map((link) => (
                    <Button key={link.to} type="text" className="justify-start text-left" block onClick={() => closeAndGo(link.to)}>
                      {link.label}
                    </Button>
                  ))}
                  <Button type="text" danger className="justify-start text-left mt-4" block icon={<LogoutOutlined />} onClick={props.store.signOut}>
                    Sign out
                  </Button>
                </div>
              </Drawer>
            </>
          )}
        </div>
      </header>
    );
  })
);

export default Header;
